import { useMemo, useState } from "react"

import {
  useFetchHomeGroupCount,
  useFetchHomeGroups,
} from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import {
  DropdownSelect,
  TSelectGroupOption,
  TSelectOption,
} from "src/ui/DropdownSelect/DropdownSelect"
import { isGroupSelectOption } from "src/ui/DropdownSelect/utils"

export function HomeLogHomeGroupFilterDropdownSelect({
  selectedOption,
  setSelectedOption,
}: {
  selectedOption: TSelectOption<string> | null
  setSelectedOption: (option: TSelectOption<string> | null) => void
}) {
  const { orgId } = useOrganization()
  const { t } = useTranslate()
  const [searchText, setSearchText] = useState("")

  const { data: totalHomeGroups } = useFetchHomeGroupCount({ orgId })

  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: {
      limit: totalHomeGroups,
      name: searchText || undefined,
    },
  })

  const groupedOptions: TSelectGroupOption[] = useMemo(() => {
    const homegroups = fetchHomeGroups.data?.homegroups ?? []

    const buildingOptions = homegroups.filter((hg) => hg.type === "building")
    const locationOptions = homegroups.filter((hg) => hg.type === "area")
    return [
      {
        title: t(langKeys.home_group_building_plural),
        options: buildingOptions.map((hg) => ({
          label: hg.name,
          value: hg.id,
        })),
      },
      {
        title: t(langKeys.organizations_home_group_plural),
        options: locationOptions.map((hg) => ({
          label: hg.name,
          value: hg.id,
        })),
      },
    ]
  }, [fetchHomeGroups.data?.homegroups, t])

  const filteredGroupOptions: TSelectGroupOption[] = useMemo(() => {
    const mapped = groupedOptions.map((opt) => {
      if (isGroupSelectOption(opt)) {
        return {
          ...opt,
          options: opt.options.filter((opt) =>
            opt.label
              ?.toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())
          ),
        }
      }
      return opt
    })

    return mapped.filter((option) => {
      if (isGroupSelectOption(option)) {
        return option.options.length > 0
      }
      return (option as TSelectOption).label
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    })
  }, [groupedOptions, searchText])

  function handleHomegroupChange(
    option: TSelectOption<string, TSelectGroupOption>
  ) {
    setSelectedOption(option)
  }

  return (
    <DropdownSelect
      label={
        selectedOption?.label ??
        `${t(langKeys.building)}/${t(langKeys.plan_renewal_estimate_breakdown_location)}`
      }
      options={filteredGroupOptions}
      selectedValue={selectedOption?.value || ""}
      onChange={({ option }) => handleHomegroupChange(option)}
      searchText={searchText}
      onSearch={setSearchText}
      placeholder={t(langKeys.search)}
      onClearSelection={() => {
        setSelectedOption(null)
        setSearchText("")
      }}
      loading={fetchHomeGroups.isLoading}
    />
  )
}
